import { motion } from 'framer-motion';
import { CheckCircle, Clock, XCircle, Circle, CreditCard, UserCheck, Building2 } from 'lucide-react';
import { Booking, BookingTimelineEntry } from '../services/bookingService';

interface BookingTrackingProps {
  booking: Booking;
  onPayNow?: () => void | Promise<void>;
}

const BookingTracking = ({ booking, onPayNow }: BookingTrackingProps) => {

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (action: string, status: string) => {
    if (action === 'BOOKING_CREATED') {
      return <Circle className="h-5 w-5" />;
    }
    if (action === 'SCHOOL_APPROVED' || status === 'U_PENDING') {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
    if (action === 'SCHOOL_REJECTED' || status === 'REJECTED') {
      return <XCircle className="h-5 w-5 text-red-500" />;
    }
    if (action === 'PAYMENT_COMPLETED' || status === 'COMPLETED') {
      return <CreditCard className="h-5 w-5 text-green-500" />;
    }
    return <Clock className="h-5 w-5 text-yellow-500" />;
  };

  const getStatusColor = (action: string, status: string, isCompleted: boolean) => {
    if (!isCompleted) {
      return 'text-ivory-400 border-ivory-300';
    }
    if (action === 'SCHOOL_REJECTED' || status === 'REJECTED') {
      return 'text-red-500 border-red-300';
    }
    if (action === 'PAYMENT_COMPLETED' || status === 'COMPLETED') {
      return 'text-green-500 border-green-300';
    }
    if (action === 'SCHOOL_APPROVED' || status === 'U_PENDING') {
      return 'text-green-500 border-green-300';
    }
    return 'text-primary border-primary';
  };

  const getStatusLabel = (action: string, status: string) => {
    switch (action) {
      case 'BOOKING_CREATED':
        return 'Enquiry Sent';
      case 'SCHOOL_APPROVED':
        return 'Approved by School';
      case 'SCHOOL_REJECTED':
        return 'Rejected by School';
      case 'PAYMENT_COMPLETED':
        return 'Payment Completed';
      default:
        return status.replace('_', ' ');
    }
  };

  const getPerformedByIcon = (performedBy: string) => {
    switch (performedBy) {
      case 'USER':
        return <UserCheck className="h-4 w-4" />;
      case 'SCHOOL':
        return <Building2 className="h-4 w-4" />;
      default:
        return <Circle className="h-4 w-4" />;
    }
  };

  // Build timeline from actionTimeline or create from status
  const timeline: BookingTimelineEntry[] = booking.actionTimeline || [];
  
  // If no timeline, create basic one from status
  if (timeline.length === 0) {
    timeline.push({
      action: 'BOOKING_CREATED',
      status: booking.status,
      performedBy: 'USER',
      performedAt: booking.submittedAt || booking.createdAt,
      notes: 'Enquiry sent',
    });
  }

  // Reverse timeline to show bottom to top (newest first)
  const reversedTimeline = [...timeline].reverse();

  // Determine current step (in original order, last item is current)
  const currentStepIndex = timeline.length - 1;

  return (
    <div className="bg-white rounded-xl border border-ivory-200 p-6">
      <h3 className="text-3 font-semibold text-ivory-900 mb-6">Booking Journey</h3>
      
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-3.5 md:left-5 top-0 bottom-0 w-0.5 bg-ivory-200" />
        
        {/* Timeline Items - Reversed order (newest first) */}
        <div className="space-y-6">
          {reversedTimeline.map((entry, reversedIndex) => {
            // Calculate original index for status checking
            const originalIndex = timeline.length - 1 - reversedIndex;
            const isCompleted = originalIndex <= currentStepIndex;
            const isCurrent = originalIndex === currentStepIndex;
            const statusColor = getStatusColor(entry.action, entry.status, isCompleted);
            
            return (
              <motion.div
                key={originalIndex}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: reversedIndex * 0.1 }}
                className="relative flex items-start gap-4"
              >
                {/* Icon */}
                <div className={`relative z-10 flex items-center justify-center w-7 h-7 md:w-10 md:h-10 rounded-full border-2 bg-white ${statusColor} ${isCurrent ? 'ring-2 ring-offset-2 ring-primary' : ''}`}>
                  {getStatusIcon(entry.action, entry.status)}
                </div>
                
                {/* Content */}
                <div className="flex-1 pt-1">
                  <div className="flex items-center justify-between mb-1">
                    <h6 className="font-semibold text-ivory-900 text-1 md:text-2">
                      {getStatusLabel(entry.action, entry.status)}
                    </h6>
                    <div className="flex items-center gap-2 text-1 text-ivory-600">
                      {getPerformedByIcon(entry.performedBy)}
                      <span className="capitalize">{entry.performedBy.toLowerCase()}</span>
                    </div>
                  </div>
                  <p className="text-1 md:text-2 text-ivory-600 mb-1">
                    {formatDate(entry.performedAt)}
                  </p>
                  {entry.notes && (
                    <p className="text-1 md:text-2 text-ivory-700 mt-2 bg-ivory-50 rounded-lg p-2">
                      {entry.notes}
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Current Status Summary */}
      <div className="mt-6 pt-6 border-t border-ivory-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-2 text-ivory-600 mb-1">Current Status</p>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-4 font-semibold text-ivory-900">
                {booking.status === 'pending' && 'Pending'}
                {booking.status === 'approved' && 'Approved'}
                {booking.status === 'rejected' && 'Rejected'}
                {booking.status === 'cancelled' && 'Cancelled'}
              </p>
              {/* Only show substatus if booking is NOT rejected */}
              {booking.substatus && booking.status !== 'rejected' && (
                <span className="text-2 text-ivory-600">
                  • {booking.substatus === 'pending approval from school' && 'Pending School Approval'}
                  {booking.substatus === 'pending payment' && 'Pending Payment'}
                </span>
              )}
            </div>
          </div>
          {booking.substatus === 'pending payment' && (
            <button
              onClick={() => {
                if (onPayNow) {
                  void onPayNow();
                }
              }}
              className="px-6 py-2.5 text-white rounded-xl font-semibold hover:shadow-xl transition-all flex items-center gap-2 shadow-lg hover:scale-105"
              style={{ backgroundColor: '#FF6B35' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FF4D1A'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF6B35'}
            >
              <CreditCard className="h-4 w-4" />
              Pay Now
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingTracking;

